<template>
  <div>
    <h3 class="title is-4 is-flex is-align-items-center">
      <span class="mr-3">{{ $t("current_status").capitalize() }}</span>
      <button
        type="button"
        class="button is-primary"
        @click="isNewPropertyStatusModalShown = true"
      >
        {{ $t("update_the_status") }}
      </button>
    </h3>

    <d-card
      v-if="latestPropertyStatus && !loading"
      class="mb-4"
      clipped
    >
      <div
        v-if="latestPropertyStatus && latestPropertyStatus !== null"
        class="b-table table-container"
      >
        <table class="table custom-tb">
          <thead>
            <tr>
              <th class="th-wrap">
                {{ $t("status").capitalize() }}
              </th>
              <th class="th-wrap">
                {{ $t("asked_rent").capitalize() }}
              </th>
              <th class="th-wrap">
                {{ $t("starting_date").capitalize() }}
              </th>
              <th class="th-wrap">
                {{ $t("key_access").capitalize() }}
              </th>
              <th class="th-wrap">
                {{ $t("availability_date_show").capitalize() }}
              </th>
              <th
                v-if="$roleCan([$laravel.role_admin])"
                class="th-wrap"
              >
                {{ $t("rent_agent_name").capitalize() }}
              </th>
              <th class="th-wrap">
                {{ $t("created_at").capitalize() }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <span class="tag">
                  {{ $t(latestPropertyStatus.type).capitalize() }}
                </span>
              </td>
              <td>
                <div
                  v-if="latestPropertyStatus.asked_rent"
                  class="is-size-7"
                >
                  {{ latestPropertyStatus.asked_rent }}$
                </div>
              </td>
              <td>
                {{ latestPropertyStatus.starting_date }}
              </td>
              <td>
                <div class="is-size-7">
                  <div v-html="latestPropertyStatus.key_access" />
                </div>
              </td>
              <td>
                <div
                  v-if="latestPropertyStatus.availability_date"
                  class="is-size-7"
                >
                  {{
                    $dayjs.utc(latestPropertyStatus.availability_date).format("LL")
                  }}
                </div>
              </td>
              <td v-if="$roleCan([$laravel.role_admin])">
                <div
                  v-if="latestPropertyStatus.rent_agents"
                  class="is-flex gap-1 is-flex-wrap-wrap"
                >
                  <span
                    v-for="rent_agent in latestPropertyStatus.rent_agents"
                    :key="rent_agent.id"
                  >
                    <b-tooltip
                      type="is-black"
                      position="is-right"
                      multilined
                    >
                      <img
                        class="is-30 is-avatar"
                        :src="rent_agent.avatar_url"
                      >

                      <template #content>
                        <div>
                          <p>
                            {{ rent_agent.name }}
                          </p>
                          <p>
                            {{ rent_agent.email }}
                          </p>
                        </div>
                      </template>
                    </b-tooltip>
                  </span>
                </div>

                <span v-else> - </span>
              </td>
              <td>
                {{
                  $dayjs.utc(latestPropertyStatus.created_at).format("LLL")
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <section
        v-else
        class="hero"
      >
        <div class="hero-body has-text-grey">
          <img
            src="/img/empty-states/data.svg"
            alt="No data"
            width="100"
            class="mb-20 is-inline-block"
          >
          <p>{{ $t("no_data") }}</p>
        </div>
      </section>
    </d-card>

    <h3 class="title is-4 mt-6">
      {{ $t("property_statuses").capitalize() }}
    </h3>

    <d-data-table
      ref="data-table"
      resource="property-statuses"
      searchable
      :url="$laravel.route(`api.property-statuses.index`, propertyId)"
      fluid
      no-padding
      :show-button="true"
      :show-url="(row) => '/properties/'+ propertyId +'/statuses/' + row.id"
      :edit-button="false"
    >
      <b-table-column
        v-slot="props"
        field="type"
        :label="$t('status').capitalize()"
        sortable
      >
        <span class="tag">
          {{ $t(props.row.type).capitalize() }}
        </span>
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="asked_rent"
        :label="$t('asked_rent').capitalize()"
      >
        <div
          v-if="props.row.asked_rent"
          class="is-size-7"
        >
          {{ props.row.asked_rent }}$
        </div>
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="starting_date"
        :label="$t('starting_date').capitalize()"
        sortable
      >
        <div class="is-size-7">
          {{ props.row.starting_date }}
        </div>
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="key_access"
        :label="$t('key_access').capitalize()"
        sortable
      >
        <div class="is-size-7">
          <div v-html="props.row.key_access" />
        </div>
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="availability_date"
        :label="$t('availability_date_show').capitalize()"
        sortable
      >
        <div
          v-if="props.row.availability_date"
          class="is-size-7"
        >
          {{ $dayjs.utc(props.row.availability_date).format("LL") }}
        </div>
      </b-table-column>

      <b-table-column
        v-if="$roleCan([$laravel.role_admin])"
        v-slot="props"
        field="rent_agent_name"
        :label="$t('rent_agent_name').capitalize()"
      >
        <div
          v-if="props.row.rent_agents"
          class="is-flex gap-1 is-flex-wrap-wrap"
        >
          <span
            v-for="rent_agent in props.row.rent_agents"
            :key="rent_agent.id"
          >
            <b-tooltip
              type="is-black"
              position="is-right"
              multilined
            >
              <img
                class="is-30 is-avatar"
                :src="rent_agent.avatar"
              >

              <template #content>
                <div>
                  <p>
                    {{ rent_agent.name }}
                  </p>
                  <p>
                    {{ rent_agent.email }}
                  </p>
                </div>
              </template>
            </b-tooltip>
          </span>
        </div>

        <span v-else> - </span>
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="created_at"
        sortable
        :label="$t('attributes.created_at').capitalize()"
      >
        <div class="is-size-7">
          {{ $dayjs.utc(props.row.created_at).format("LLL") }}
        </div>
      </b-table-column>
    </d-data-table>

    <v-status
      class="has-text-left is-flex is-justify-content-flex-start"
      :property="property"
      :current-status="status"
      :is-shown="isShownStatus"
      @success="onSuccessUpdatePropertyStatus"
      @close="isShownStatus=false"
    />

    <b-modal
      v-model="isNewPropertyStatusModalShown"
      :width="600"
      @close="isNewPropertyStatusModalShown = false"
    >
      <div class="modal-card">
        <header
          class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
        >
          <div
            class="is-flex is-full pb-4 is-align-content-space-between"
            style="width: 100%"
          >
            <p class="modal-card-title title mb-0">
              {{ $t("modal_status_update_in_property_list") }}
            </p>
            <button
              class="delete"
              aria-label="close"
              @click="isNewPropertyStatusModalShown = false"
            />
          </div>
          <div
            v-if="latestPropertyStatus"
            class="modal-card-subtitle"
          >
            <p class="is-size-7">
              {{ $t("modal_message_first_part") }}
              {{ $shortStreet(property.address.address_1) }}
              {{ property.address.address_2 }}
              ({{ property.address.city }})
              {{ $t("modal_message_second_part") }}
              <span class="has-text-weight-bold">
                {{ $t(latestPropertyStatus.type) }}
              </span>
              <span v-if="newPropertyStatus">
                {{ $t("modal_message_third_part") }}
                {{ $t("from") }}
                <span class="has-text-weight-bold">
                  {{ $t(latestPropertyStatus.type) }}
                </span>
                {{ $t("to") }}
                <span class="has-text-weight-bold">
                  {{ $t(newPropertyStatus) }}
                </span>
                {{ $t("modal_message_forth_part") }}
              </span>
            </p>
          </div>
        </header>
        <section class="modal-card-body">
          <v-popup-form
            :property="property"
            @success="success"
            @status="updateNewStatus"
            @close="cancelCreatePropertyStatus"
          />
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import form_popup from "../property/_form_popup.vue";
import ActivateForRentStatusComponent from "../property/components/ActivateForRentStatusComponent.vue";

import PropertyStatusPopup from "../property/components/PropertyStatusPopup.vue";

export default {
  components: {
    "v-popup-form": form_popup,
    "v-activate-property-status": ActivateForRentStatusComponent,
    "v-status": PropertyStatusPopup,
  },
  props: {
    propertyId: {
      required: true,
      type: Number,
    },
    property: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      isShow: false,
      isShownCurrentStatus: false,
      isShownStatus: false,
      isNewPropertyStatusModalShown: false,
      loading: true,
      latestPropertyStatus: null,
      newPropertyStatus: null,
      status:null,
    };
  },
  created() {
    this.fetchLastStatus();
  },
  methods: {
    fetchLastStatus() {
      this.loading = true;
      this.$http
        .get(
          route("api.property.latest_status", {
            property: this.propertyId,
          })
        )
        .then((response) => {
          this.loading = false;
          this.latestPropertyStatus = response.data.data;
        });
      this.loading = false;
    },
    success(event) {
      this.fetchLastStatus();
      this.$refs["data-table"].loadData();
      this.isNewPropertyStatusModalShown = false;
    },
    cancelCreatePropertyStatus() {
      this.isNewPropertyStatusModalShown = false;
    },
    updateNewStatus(newStatus) {
      this.newPropertyStatus = newStatus;
    },
    onSuccessActivatePropertyStatusForRent() {
      this.fetchLastStatus();
    },
    onSuccessUpdatePropertyStatus() {
      this.fetchLastStatus();
      this.$refs["data-table"].loadData();
      this.isShown = false;
    },
    showUpdateStatus(status) {
      console.log(status);
      this.status = status;
      this.isShownStatus = true;
    },
  },
};
</script>

<style scoped>
.custom-tb tbody tr td {
  font-size: 14px !important;
}
</style>
